import ahoy from "ahoy.js";
import type { Article, ConversationTurnRouting, Organization, Question } from "../types";
import { getUserId } from "./identification";
import { getSearchInteractionId } from "./interactions";
import { getSessionId } from "./sessions";
import { getSocketConnectionId } from "./socket_connections";
import { getVisitorId } from "./visitors";

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Event Details

export enum AnalyticsEvent {
  SEARCH_INITIALIZED = "search_initialized",
  SEARCH_WIDGET_CLICKED = "search_widget_clicked",
  SEARCH_RESULTS_GENERATED = "search_results_generated",
  SEARCH_RESULTS_CLICKED = "search_results_clicked",
  SEARCH_ABANDONED = "search_abandoned",
}

export type SearchResultsGeneratedProperties = {
  query: string;
  query_type: ConversationTurnRouting | undefined;
  query_results: {
    answer?: string;
    questions?: Question[];
    articles?: Article[];
  };
};

export type SearchResultsClickedProperties = {
  query: string;
  query_type: ConversationTurnRouting | undefined;
  result_type: "question" | "article" | "interaction";
  result_value: Article | Question;
};

export type SearchAbandonedProperties = {
  query: string;
  query_type: ConversationTurnRouting | undefined;
};

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Tracking

export function track(event: AnalyticsEvent.SEARCH_INITIALIZED, organization: Organization): void;
export function track(event: AnalyticsEvent, organization: Organization, properties: Record<string, unknown>): void;
export function track(
  event: AnalyticsEvent,
  organization: Organization,
  properties: Record<string, unknown> = {},
): void {
  // Default tracking values
  const visitorId = getVisitorId();
  const sessionId = getSessionId();
  const searchInteractionId = getSearchInteractionId();
  const socketConnectionId = getSocketConnectionId();
  const onPage = getOnPageValue();

  // Build the final properties payload
  const payload: Record<string, unknown> = {
    ...properties,
    visitor_id: visitorId,
    session_id: sessionId,
    organization_id: organization.id,
    organization: organization.name,
    on_page: onPage,
    client_triggered_at: Date.now(),
    socket_connection_id: socketConnectionId,
    external_user_id: getUserId(),
    source: "client",
  };

  // Only include search_interaction_id if it exists
  if (searchInteractionId) {
    payload.search_interaction_id = searchInteractionId;
  }

  if (import.meta.env.DEV) {
    console.log("Tracking Event", event, payload);
  }
  ahoy.track(event, payload);
}

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Helper Methods

export function getOnPageValue(): string {
  // Get the current URL without the query string
  return window.location.href.split("?")[0];
}
